import { Component, Inject, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import {
  DwComponent,
  DwComponentType,
  DwExpressionService,
  DwMenuService,
  DwMetaDataFormApi,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwSectionBaseComponent,
  DwUiConfigRegistryService,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken
} from '@devwareapps/devware-cap';
import { Observable, Subject } from 'rxjs';
import { debounceTime, finalize, takeUntil } from 'rxjs/operators';

import {
  AppMetaData,
  AppMetaDataItemNames,
  AssignmentCourseLessonEntity,
  AssignmentEntity,
  StudentEntity
} from '../../../../meta-data/app-meta-data.service';
import { CourseRepositoryService } from '../../../courses/services/course-repository.service';
import { StudentRepositoryService } from '../../../students/services/student-repository.service';
import { AssignmentVariableContext } from '../../models/assignment-variable-context.model';
import { AssignmentResetImpact } from '../../../schools/models/assignment-reset-impact.model';
import { AssignmentResetImpactService } from '../../../schools/services/assignment-reset-impact.service';

@DwComponent({
  key: 'lesson-selection-list-control',
  name: 'Lesson Selection List Control',
  componentType: DwComponentType.formSection,
  isGlobal: false,
  parentItemName: AppMetaDataItemNames.Assignment
})
@Component({
  selector: 'app-lesson-selection-list-form-control',
  template: `
    <div class="lesson-selection-progress-state" *ngIf="!hasRequiredInfo">
      <i class="fa fa-book" aria-hidden="true"></i>
      Please select a course to choose lessons.
    </div>
    <div class="lesson-tree-container" *ngIf="hasRequiredInfo && (resetImpact$ | async) as resetImpact">
      <div class="lesson-selection-progress-state" *ngIf="courseHierarchyLoading && resetImpact.EvaluatedStudentCount > 0">
        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
        Loading Course Hierarchy...
      </div>
      <app-lesson-selection-list-control [courseId]="courseId" [(selectedLessonIds)]="selectedLessonIds" (selectedLessonIdsChange)="updateLessons()"
       [showStudentProgress]="false" [resetImpact]="resetImpact"
       *ngIf="!courseHierarchyLoading && resetImpact.IsAvailable
         && (resetImpact.IsLoading || resetImpact.IsRefreshing || resetImpact.EvaluatedStudentCount > 0)">
      </app-lesson-selection-list-control>
      <div class="lesson-selection-progress-state" *ngIf="!resetImpact.IsLoading && !resetImpact.IsAvailable">
        Student progress could not be loaded. Change the course or student selection to try again.
      </div>
      <div class="lesson-selection-progress-state"
        *ngIf="!resetImpact.IsLoading && !resetImpact.IsRefreshing && resetImpact.IsAvailable && resetImpact.EvaluatedStudentCount === 0">
        Select at least one student who has this course to choose lessons.
      </div>
      <div class="invalid-feedback lesson-selection-validation" *ngIf="showLessonValidationMessage">
        At least one lesson must be selected.
      </div>
    </div>
  `,
  styles: [`
    .lesson-selection-validation {
      display: block;
      margin-top: var(--space-2, 8px);
    }
    .lesson-selection-progress-state {
      padding: var(--space-4, 16px);
      margin-bottom: var(--space-4, 16px);
      border: 1px solid var(--border-subtle);
      border-radius: var(--radius-md);
      color: var(--text-secondary);
      text-align: center;
    }
    .lesson-selection-progress-state .fa {
      margin-right: var(--space-2, 8px);
    }
  `]
})
export class LessonSelectionListFormControlComponent extends DwSectionBaseComponent implements OnDestroy {
  associativeList: AbstractControl;

  controlName: string = 'AssignmentCourseLesson';

  courseId: number;

  selectedLessonIds: number[] = [];
  courseIdFC: FormControl;
  formApi: DwMetaDataFormApi;
  variableContext: AssignmentVariableContext;

  studentId: number;
  showStudentProgress: boolean = false;
  hasRequiredInfo: boolean = false;
  showLessonValidationMessage = false;
  courseHierarchyLoading = false;
  resetImpact$: Observable<AssignmentResetImpact>;
  private loadedHierarchyCourseId: number;
  private loadingHierarchyCourseId: number;
  private readonly destroy$ = new Subject<void>();

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    private dwFormStateService: DwMetaDataFormStateService,
    private dwMenuService: DwMenuService,
    private studentRepositoryService: StudentRepositoryService,
    private courseRepositoryService: CourseRepositoryService,
    private assignmentResetImpactService: AssignmentResetImpactService
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.associativeList = this.formGroup.get(this.controlName);
    this.courseIdFC = this.formGroup.get('CourseId') as FormControl;
    this.resetImpact$ = this.assignmentResetImpactService.observe(this.formApi);

    if (!this.associativeList) {
      console.warn(`Form control ${this.controlName} not found in form group. This is required for the lesson selection list control to work.`);
      return;
    }

    this.configureLessonValidator();

    this.subscriptions.push(
      this.associativeList.statusChanges.subscribe(() => this.updateLessonValidationMessage())
    );

    this.updateRequiredInfoState();

    this.courseIdFC?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateRequiredInfoState());

    this.runAfterFormDataLoaded(() => {
      this.updateRequiredInfoState();
      this.initSelectedStudent();
    });

    this.formApi.getFormDataChanges()
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateRequiredInfoState();
        this.setupSelectedLessons();

        this.updateSelectedStudent();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateRequiredInfoState() {
    this.courseId = this.resolveCourseId();
    this.hasRequiredInfo = !!this.courseId;
    this.preloadCourseHierarchy();
  }

  private preloadCourseHierarchy(): void {
    const courseId = this.courseId;
    if (!courseId || this.loadedHierarchyCourseId === courseId || this.loadingHierarchyCourseId === courseId) {
      return;
    }

    this.loadingHierarchyCourseId = courseId;
    this.courseHierarchyLoading = true;
    this.courseRepositoryService.getCourseHierarchy(courseId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (this.loadingHierarchyCourseId === courseId) {
            this.loadingHierarchyCourseId = null;
            this.courseHierarchyLoading = false;
          }
        })
      )
      .subscribe(() => this.loadedHierarchyCourseId = courseId);
  }

  setupSelectedLessons() {
    const allLessons: AssignmentCourseLessonEntity[] = this.associativeList.value || [];

    this.selectedLessonIds = allLessons.filter(l => !l._isDeleted).map(l => l.CourseLessonId);
  }

  updateSelectedStudent() {
    this.variableContext = this.resolveVariableContext();
    const assignment = this.formApi.getFormData<AssignmentEntity>();

    this.showStudentProgress = false;

    const currentStudents = assignment?.AssignmentStudent?.filter(as => !as._isDeleted) || [];

    if (currentStudents.length <= 1 || (this.variableContext?.ShowStudentProgressWhenMultiple && currentStudents.length > 0)) {
      const studentId = currentStudents[0]?.StudentId;

      if (this.variableContext && this.variableContext.StudentId != studentId) {
        this.variableContext.StudentId = studentId;
        this.studentId = studentId;

        this.formApi.patchVariableContext({ StudentId: studentId });
      } else {
        this.studentId = studentId;
      }

      if (!assignment?.UseGroupAssignment && studentId) {
        this.showStudentProgress = true;
      }
    }
  }

  initComplete = false;

  initSelectedStudent() {
    if (this.initComplete) {
      return;
    }

    this.initComplete = true;

    const paramMap = (this.dwMenuService.menuState.currentParams as any)?.map;

    if (!paramMap) {
      return;
    }

    const userId = paramMap['dw_UserId'];

    if (userId) {
      return this.studentRepositoryService.getStudentFromUserId(userId).subscribe(student => {
        this.setInitialStudent(student);
      });
    }
  }

  setInitialStudent(student: StudentEntity) {
    this.variableContext = this.resolveVariableContext();
    const assignment = this.formApi.getFormData<AssignmentEntity>();

    if (student?.StudentId && (assignment?.AssignmentStudent?.length || 0) == 0) {
      this.studentId = student.StudentId;

      const assignmentStudent = AppMetaData.AssignmentStudent.CreateEntity();
      assignmentStudent.StudentId = student.StudentId;

      this.formApi.patchFormData({ AssignmentStudent: [assignmentStudent] });

      if (this.variableContext) {
        this.variableContext.StudentId = student.StudentId;
        this.formApi.patchVariableContext({ StudentId: student.StudentId });
      }
    }
  }

  updateLessons() {
    const allLessons: AssignmentCourseLessonEntity[] = this.associativeList.value || [];

    for (let lesson of allLessons) {
      lesson._isDeleted = true;
    }

    for (let lessonId of this.selectedLessonIds) {
      let lesson = allLessons.find(l => l.CourseLessonId == lessonId);

      if (lesson) {
        lesson._isDeleted = false;
      } else {
        lesson = AppMetaData.AssignmentCourseLesson.CreateEntity();

        lesson.CourseLessonId = lessonId;

        allLessons.push(lesson);
      }
    }

    this.associativeList.setValue(allLessons);
    this.associativeList.markAsTouched();
    this.updateLessonValidationMessage();
  }

  protected submittedChanged(): void {
    this.updateLessonValidationMessage();
  }

  private updateLessonValidationMessage(): void {
    this.showLessonValidationMessage = !!this.submitted
      && !!this.associativeList?.errors?.lessonCountError;
  }

  private configureLessonValidator(): void {
    const existingValidator = this.associativeList.validator;
    const lessonValidator = (control: AbstractControl) => {
      const lessons = (control.value || []) as AssignmentCourseLessonEntity[];
      const hasSelectedLesson = lessons.some(lesson => lesson._isDeleted !== true && Number(lesson.CourseLessonId) > 0);

      return hasSelectedLesson ? null : { lessonCountError: { value: true } };
    };

    this.associativeList.setValidators(existingValidator ? [existingValidator, lessonValidator] : [lessonValidator]);
    this.associativeList.updateValueAndValidity({ emitEvent: false });
  }

  private resolveCourseId(): number {
    const courseId = this.courseIdFC?.value || this.formApi.getFormData<AssignmentEntity>()?.CourseId;

    return courseId ? Number(courseId) : null;
  }

  private resolveVariableContext(): AssignmentVariableContext {
    try {
      return this.formApi?.getVariableContextValue<AssignmentVariableContext>();
    } catch {
      return null;
    }
  }
}
